import { useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setJourneyContext, selectHasSkipped, selectCurrentStep } from 'features/onboarding/reducers/onboardingSlice';
import { useAuth0 } from '@auth0/auth0-react';
import { signupApi, SignupStatus, type SignupStatusResponse } from 'features/frictionless-signup/services/signupApi';
import { JOURNEY_CONFIGS, DEFAULT_JOURNEY } from 'features/frictionless-signup/config/journeyConfig';

/**
 * Result returned by the {@link useOnboardingCheck} hook
 */
interface OnboardingCheckResult {
  /** Whether the user needs to complete onboarding */
  needsOnboarding: boolean;
  /** Whether the check is in progress */
  isLoading: boolean;
  /** Error message if the check failed */
  error: string | null;
  /** Current onboarding step to display */
  currentStep: 'snowflake_setup' | 'runner_provision' | null;
}

/**
 * Journey context containing app information
 */
interface JourneyContext {
  /** Display name of the application */
  app_name: string;
  /** Journey variant identifier */
  app_variant: string;
  /** URL path to redirect to after onboarding */
  redirect_to: string;
}

/**
 * Helper function to retrieve journey context from signup status response
 *
 * This function uses the app_variant from the status endpoint to determine
 * the user's journey. If app_variant is not provided or invalid, it falls back
 * to the default "Onboarding" journey.
 *
 * @param signupStatus - The signup status response from the API
 * @returns Journey context based on app_variant or default fallback
 */
function getJourneyContext(signupStatus: SignupStatusResponse | null): JourneyContext {
  // If status exists and has app_variant, use it to look up journey config
  if (signupStatus?.app_variant != null && signupStatus.app_variant in JOURNEY_CONFIGS) {
    const config = JOURNEY_CONFIGS[signupStatus.app_variant];
    return {
      app_name: config.app_name,
      app_variant: config.app_variant,
      redirect_to: config.redirect_to,
    };
  }

  // Fall back to default "Onboarding" journey for 404 or missing app_variant
  const defaultConfig = JOURNEY_CONFIGS[DEFAULT_JOURNEY];
  return {
    app_name: defaultConfig.app_name,
    app_variant: defaultConfig.app_variant,
    redirect_to: defaultConfig.redirect_to,
  };
}

/**
 * useOnboardingCheck Hook
 *
 * Determines if the current user needs onboarding by checking their signup status
 * via the signup status endpoint. This hook intelligently handles journey context
 * that may already be available from the signup flow.
 *
 * This hook is typically used in the AdminView to determine whether to redirect
 * users to the onboarding flow and which step to show.
 *
 * Journey Context Handling:
 * - Journey context persists from signup flow via Redux (automatically set when signup succeeds)
 * - If context already exists in Redux, it's preserved and API provides status updates only
 * - If no context exists, fetches it from API based on app_variant in signup record
 * - Falls back to default "Onboarding" journey if API returns 404 or no app_variant
 *
 * Behavior:
 * - Checks Redux state first to avoid unnecessary API calls during transitions
 * - If onboarding is complete in Redux, skips API check to prevent race conditions
 * - Fetches user's signup status from API when needed
 * - If no signup exists (null), user doesn't need onboarding
 * - Maps signup status to onboarding steps:
 *   - SIGNUP_CREATED -> snowflake_setup
 *   - SNOWFLAKE_ACCOUNT_CREATED -> runner_provision
 *   - RUNNER_* -> runner_provision
 *   - SIGNUP_COMPLETED -> no onboarding needed
 * - Stores journey context in Redux (if not already present from signup)
 * - Uses ref to prevent duplicate checks
 *
 * @hook
 * @example
 * ```tsx
 * function Dashboard() {
 *   const { needsOnboarding, isLoading, currentStep } = useOnboardingCheck();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   if (needsOnboarding) {
 *     return <Navigate to="/onboarding" />;
 *   }
 *
 *   return <DashboardContent />;
 * }
 * ```
 *
 * @returns Object containing onboarding status, loading state, current step, and error
 */
export const useOnboardingCheck = (): OnboardingCheckResult => {
  const dispatch = useAppDispatch();
  const { user, isLoading: authLoading } = useAuth0();
  const hasSkipped = useAppSelector(selectHasSkipped);
  const reduxCurrentStep = useAppSelector(selectCurrentStep);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [currentStep, setCurrentStep] = useState<'snowflake_setup' | 'runner_provision' | null>(null);

  // Use ref instead of state to prevent re-renders and ensure synchronous checks
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    const checkOnboardingStatus = async (): Promise<void> => {
      console.log(
        '[useOnboardingCheck] Effect triggered - authLoading:',
        authLoading,
        'hasChecked:',
        hasCheckedRef.current,
        'user:',
        user?.sub,
        'reduxStep:',
        reduxCurrentStep,
      );

      //  Don't check if still authenticating or already checked
      if (authLoading || hasCheckedRef.current) {
        console.log('[useOnboardingCheck] Skipping - already checked or still loading');
        return;
      }

      // If Redux state shows onboarding is complete, skip API check to avoid race condition
      if (reduxCurrentStep === 'complete') {
        console.log('[useOnboardingCheck] Redux shows complete - skipping API check to avoid race condition');
        hasCheckedRef.current = true;
        setNeedsOnboarding(false);
        setCurrentStep(null);
        setIsLoading(false);
        return;
      }

      // Mark as checked IMMEDIATELY to prevent race conditions
      hasCheckedRef.current = true;
      console.log('[useOnboardingCheck] Starting onboarding check...');

      try {
        setIsLoading(true);
        setError(null);

        // Fetch signup status for current user
        const signupStatus = await signupApi.getCurrentUserSignupStatus();

        console.log('[useOnboardingCheck] Signup status:', signupStatus);

        // If no signup exists (404), user doesn't need onboarding
        if (signupStatus === null) {
          console.log('[useOnboardingCheck] No signup found (404) - no onboarding needed');
          setNeedsOnboarding(false);
          setCurrentStep(null);
          return;
        }

        // Map signup status to onboarding requirements
        switch (signupStatus.current_step) {
          case SignupStatus.SIGNUP_CREATED: {
            // User needs to connect Snowflake account
            console.log('[useOnboardingCheck] SIGNUP_CREATED - show snowflake setup');
            setNeedsOnboarding(true);
            setCurrentStep('snowflake_setup');
            // Get journey context from status response and store it
            const journeyContext = getJourneyContext(signupStatus);
            dispatch(setJourneyContext(journeyContext));
            break;
          }

          case SignupStatus.SNOWFLAKE_ACCOUNT_CREATED:
          case SignupStatus.RUNNER_PROVISIONING:
          case SignupStatus.RUNNER_PROVISIONING_FAILED:
          case SignupStatus.RUNNER_PROVISIONED: {
            // User needs to provision runner
            console.log('[useOnboardingCheck] Runner step - show runner provision');
            setNeedsOnboarding(true);
            setCurrentStep('runner_provision');
            // Get journey context from status response and store it
            const journeyCtx = getJourneyContext(signupStatus);
            dispatch(setJourneyContext(journeyCtx));
            break;
          }

          case SignupStatus.SIGNUP_COMPLETED: {
            // Onboarding complete, show dashboard
            console.log('[useOnboardingCheck] SIGNUP_COMPLETED - no onboarding needed');
            setNeedsOnboarding(false);
            setCurrentStep(null);
            break;
          }

          default: {
            console.warn('[useOnboardingCheck] Unknown signup status:', signupStatus.current_step);
            setNeedsOnboarding(false);
            setCurrentStep(null);
          }
        }
      } catch (err) {
        console.error('[useOnboardingCheck] Error checking onboarding status:', err);
        setError('Failed to check onboarding status');
        setNeedsOnboarding(false);
        setCurrentStep(null);
      } finally {
        setIsLoading(false);
        console.log('[useOnboardingCheck] Check complete');
      }
    };

    void checkOnboardingStatus();
  }, [authLoading, dispatch, user, reduxCurrentStep]);

  return {
    needsOnboarding: needsOnboarding && !hasSkipped,
    isLoading: authLoading || isLoading,
    error,
    currentStep,
  };
};
